import { NamedArray } from './namedarray';
import { IAttribute, IModel } from './annotation';
import { Metamodel } from './model';
import { Named, Owned } from './named';
import { UniqueArray } from './uniquearray';
import { OwnedSet } from './ownedset';
import _ from 'lodash';
import { autosaveEnabledTypes } from '../../constants/autosave-constants';

export class Copier {

    // eslint-disable-next-line brace-style
    private constructor() { }

    /*
        Replaces a clone back to the original. Only select targetNameds or no target nameds
        can be passed in.
        Makes a clone of the object with similar identities. This should
        be used within editors which requiring backing the object but
        not necessarily saving it. The clone's owner will be detached from the
        original owner. If the list of nameds have mutual dependencies, they are rewired
        and others are retained depending on whether the dependency is external or internal
        The named can either be concept objects(pipeline, dataflow etc) or other contained objects
        (activity, column etc). You cannot pass a object and one of its contained objects together
        in the same clone call.
        Notice: copyState works only when its value is true and the related resources support autosave
    */
    public static clone(context: { inlineDataset?: boolean }, sourceNameds: Named[], targetNameds?: Named[], useDiffId?: boolean, copyState?: boolean): Named[] {
        // This contains all the passed named + any referencible nameds that are encountered
        const objectQueue = new Array<{}>();
        const cloneQueue = new Array<{}>();

        // Things we have to return cloneOver
        const clonedNameds = new Array<Named>();

        const sourceConceptNameds = new Set<Named>();

        // Has all the referable objects
        const referableMap = new Map<{}, {}>();

        for (let index = 0; index < sourceNameds.length; index++) {
            const object = sourceNameds[index];
            const concept = object.concept;
            if (concept) {
                sourceConceptNameds.add(concept as Named);
            }
            const model = Metamodel.modelOf(object);
            const clone = this.mirror(object, targetNameds && index in targetNameds ? targetNameds[index] : undefined, useDiffId);
            objectQueue.push(object);
            cloneQueue.push(clone);
            clonedNameds.push(clone as Named);
            if (model.referable) {
                referableMap.set(object, clone);
            }
        }

        let original: {} | undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let clone: any;

        let model: IModel;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let value: any;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let valueClone: any;

        let propertyNames: string[];
        let clonePropertyNames: string[];

        let originalModel: IModel;

        let attribute: IAttribute | undefined;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let cloneArray: any[] | undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let cloneSet: Set<any> | undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let cloneMap: Map<any, any> | undefined;

        // First copy over all the contained attributes and leave the references for the next pass

        // eslint-disable-next-line no-cond-assign
        while ((original = objectQueue.pop())) {
            originalModel = Metamodel.modelOf(original);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            clone = cloneQueue.pop() as any;
            propertyNames = Object.getOwnPropertyNames(original);
            clonePropertyNames = Object.getOwnPropertyNames(clone);

            // Remove all things not applicable
            for (const cloneProperty of clonePropertyNames) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (!(cloneProperty in original) || (Copier.isVoid((original as any)[cloneProperty]))) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    delete (clone as any)[cloneProperty];
                }
            }

            for (const property of propertyNames) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value = (original as any)[property];
                attribute = originalModel.hasAttribute(property) ? originalModel.attribute(property) : undefined;
                if (Copier.isVoid(value)) {
                    continue;
                }

                if (attribute?.transient) {
                    continue;
                }

                // Contained and attribute is reference or !contained and it is not a reference
                if (attribute?.isReferenced(context)) {
                    continue;
                }

                if (_.isArray(value)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    cloneArray = (clone as any)[property];
                    cloneArray = cloneArray ? cloneArray : Copier.newArray(value, clone);
                    clone[property] = cloneArray;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Copier.cloneContainedArray(value as any[], cloneArray, objectQueue, cloneQueue, referableMap);
                } else if (_.isSet(value)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    cloneSet = (clone as any)[property];
                    cloneSet = cloneSet ? cloneSet : Copier.newSet(value, clone);
                    clone[property] = cloneSet;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Copier.cloneContainedSet(value as Set<any>, cloneSet, objectQueue, cloneQueue, referableMap);
                } else if (_.isMap(value)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    cloneMap = (clone as any)[property];
                    cloneMap = cloneMap ? cloneMap : Copier.newMap(value, clone);
                    clone[property] = cloneMap;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Copier.cloneContainedMap(value as Map<any, any>, cloneMap, objectQueue, cloneQueue, referableMap);
                } else {
                    valueClone = clone[property];
                    if (!Metamodel.isModelOf(value)) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        clone[property] = value;
                        continue;
                    }

                    model = Metamodel.modelOf(value);

                    if (Copier.isVoid(valueClone) || !Owned.MODEL.isAncestor(model) || (valueClone.id !== value.id) || !Metamodel.isModelOf(valueClone)) {
                        valueClone = this.mirror(value);
                    }

                    // Unlike collection owner is set explicitly
                    if (Owned.MODEL.isAncestor(model) && Owned.MODEL.isAncestor(Metamodel.modelOf(clone))) {
                        Owned.own(clone as Owned, property, valueClone);
                    }

                    objectQueue.push(value);
                    cloneQueue.push(valueClone);

                    if (model.referable) {
                        referableMap.set(value, valueClone);
                    }

                    clone[property] = valueClone;
                }
            }
        }

        // Restore the initial queues preparing for reference resolution

        for (let index = 0; index < sourceNameds.length; index++) {
            const object = sourceNameds[index];
            objectQueue.push(object);
            cloneQueue.push(clonedNameds[index]);
        }

        // Handle references but make sure to traverse the contained objects to see additional references
        // in them including arrays and sets
        // eslint-disable-next-line no-cond-assign
        while ((original = objectQueue.pop())) {
            originalModel = Metamodel.modelOf(original);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            clone = cloneQueue.pop() as any;
            propertyNames = Object.getOwnPropertyNames(original);
            clonePropertyNames = Object.getOwnPropertyNames(original);

            for (const property of propertyNames) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value = (original as any)[property];
                attribute = originalModel.hasAttribute(property) ? originalModel.attribute(property) : undefined;
                if (Copier.isVoid(value)) {
                    continue;
                }

                if (attribute?.transient) {
                    continue;
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                valueClone = (clone as any)[property];

                // Contained and attribute is reference or !contained and it is not a reference
                if (!attribute || !attribute.isReferenced(context)) {
                    // We need to add all the contained ones to continue the scan. Including value classes
                    // Unfortunately value classes for set are undetermined so we just have to rely on id
                    if (_.isArray(value)) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        Copier.mapContainedArray(value as any[], valueClone as any[], objectQueue, cloneQueue);
                    } else if (_.isSet(value)) { // Today for set we only support owned sets with ids
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        Copier.mapContainedSet(value as Set<any>, valueClone as Set<any>, objectQueue, cloneQueue, referableMap);
                    } else if (_.isMap(value)) { // Today for set we only support owned sets with ids
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        Copier.mapContainedMap(value as Map<any, any>, valueClone as Map<any, any>, objectQueue, cloneQueue, referableMap);
                    } else {
                        if (!Metamodel.isModelOf(value)) {
                            continue;
                        }
                        objectQueue.push(value);
                        cloneQueue.push(valueClone);
                    }

                    continue; // We are out of here
                }

                if (_.isArray(value)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    cloneArray = (clone as any)[property];
                    cloneArray = cloneArray ? cloneArray : Copier.newArray(value, clone);
                    clone[property] = cloneArray;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Copier.cloneReferencedArray(value as any[], cloneArray, referableMap, sourceConceptNameds);
                } else if (_.isSet(value)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    cloneSet = (clone as any)[property];
                    cloneSet = cloneSet ? cloneSet : Copier.newSet(value, clone);
                    clone[property] = cloneSet;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Copier.cloneReferencedSet(value as Set<any>, cloneSet, referableMap, sourceConceptNameds);
                } else if (_.isMap(value)) { // Today for set we only support owned sets with ids
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Copier.cloneReferencedMap(value as Map<any, any>, valueClone as Map<any, any>, referableMap, sourceConceptNameds);
                } else {
                    // check whether value is a model value
                    // cover null reference value, e.g. AKV references to a null linkedservice
                    if (!Metamodel.isModelOf(value)) {
                        clone[property] = value;
                        continue;
                    }
                    model = Metamodel.modelOf(value);
                    valueClone = referableMap.get(value);
                    if (!valueClone && Owned.MODEL.isInstance(value)) {
                        const concept = (value as Owned).concept as Named;
                        // If we are copying in the vicinity of the concept objects passed in
                        // we copy else we reference what the source has
                        valueClone = (concept && !sourceConceptNameds.has(concept)) ? value : undefined;
                    }

                    if (valueClone) {
                        clone[property] = valueClone;
                    } else if (property in clone) {
                        delete clone[property];
                    }
                }
            }

            // deep clone state
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (copyState && original && clone && (original as any).extension && (original as any).extension._state$) {
                const conceptName = originalModel && originalModel.concept ? originalModel.concept.name : '';
                if (autosaveEnabledTypes.includes(conceptName)) {
                    Copier.copyState(original as Named, clone as Named);
                }
            }
        }
        return clonedNameds;
    }

    /*
        Makes a full copy of the object that is disconnected from the original.
        Other than that it has all the characteristics of clone
    */
    public static copy(...nameds: Named[]): Named[] {
        return nameds;
    }

    private static mirror(object: {}, clone?: {}, useDiffId?: boolean, owner?: Owned): {} {
        const model = Metamodel.modelOf(object);
        const mirror = clone ? clone : (owner ? Owned.create<Owned>(model, owner) : model.create());

        if (Named.MODEL.isInstance(object)) {
            if (!useDiffId) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (mirror as any)[Named.idProperty] = (object as Named).id;
            }
        }

        return mirror;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static newArray(value: any[], clone: {}): any[] {
        if (value instanceof NamedArray) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return NamedArray.create<any>(clone instanceof Owned ? clone as Owned : undefined);
        } else if (value instanceof UniqueArray) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return UniqueArray.createUniqueArray<any>();
        }
        return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static newSet(value: Set<any>, clone: {}): Set<any> {
        if (value instanceof OwnedSet) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return OwnedSet.create<any>(clone instanceof Owned ? clone as Owned : undefined);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return new Set<any>();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static newMap(_value: Map<any, any>, _clone: {}): Map<any, any> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return new Map<any, any>();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static cloneContainedArray(valueArray: any[], cloneArray: any[],
        objectQueue: Array<{}>, cloneQueue: Array<{}>, referableMap: Map<{}, {}>): void {
        if (valueArray.length === 0 && cloneArray.length === 0) {
            return;
        }

        const cloneNamedMap = new Map<number, Named>();

        // Store old values to lookup
        for (const clone of cloneArray) {
            if (clone == null || !Named.MODEL.isInstance(clone)) {
                continue;
            }
            cloneNamedMap.set((clone as Named).id, clone);
        }

        cloneArray.splice(0, cloneArray.length);

        let cloneValue: {} | undefined;
        // Walk through existing array entries
        for (const value of valueArray) {
            if (!Metamodel.isModelOf(value)) {
                cloneArray.push(value);
                continue;
            }

            const model = Metamodel.modelOf(value);
            cloneValue = Named.MODEL.isAncestor(model) ? cloneNamedMap.get(value.id) : undefined; // We still want to copy over here
            if (!cloneValue) {
                // For the clone object that we are about to create, the parent is already created.
                // Fetch the owner/parent from referableMap.
                let newOwner: Owned;
                const valueOwned = value as Owned;
                if (valueOwned) {
                    newOwner = referableMap.get(valueOwned.owner) as Owned;
                }
                cloneValue = this.mirror(value, undefined, false, newOwner);
            }

            objectQueue.push(value);
            cloneQueue.push(cloneValue);

            if (model.referable) {
                referableMap.set(value, cloneValue);
            }

            cloneArray.push(cloneValue);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static cloneReferencedArray(valueArray: any[], cloneArray: any[],
        referableMap: Map<{}, {}>, sourceConceptNameds: Set<Named>): void {

        if (valueArray.length === 0 && cloneArray.length === 0) {
            return;
        }

        cloneArray.splice(0, cloneArray.length);

        let cloneValue: {} | undefined;
        // Walk through existing array entries
        for (const value of valueArray) {
            // Ignore undefined value when copying.
            if (value !== undefined) {
                const model = Metamodel.modelOf(value);
                cloneValue = referableMap.get(value);

                if (!cloneValue && Owned.MODEL.isAncestor(model)) {
                    const concept = (value as Owned).concept as Named;
                    // If we are copying in the vicinity of the concept objects passed in
                    // we copy else we reference what the source has
                    cloneValue = (concept && !sourceConceptNameds.has(concept)) ? value : undefined;
                }

                if (cloneValue) {
                    cloneArray.push(cloneValue);
                }
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static cloneContainedSet(valueSet: Set<any>, cloneSet: Set<any>,
        objectQueue: Array<{}>, cloneQueue: Array<{}>, referableMap: Map<{}, {}>): void {

        if (valueSet.size === 0 && cloneSet.size === 0) {
            return;
        }

        const cloneNamedMap = new Map<number, Named>();

        // Store old values to lookup
        for (const clone of cloneSet) {
            if (!Named.MODEL.isInstance(clone)) {
                continue;
            }
            cloneNamedMap.set((clone as Named).id, clone);
        }

        cloneSet.clear();

        let cloneValue: {} | undefined;
        // Walk through existing array entries
        for (const value of valueSet) {
            if (!Metamodel.isModelOf(value)) {
                cloneSet.add(value);
                continue;
            }

            const model = Metamodel.modelOf(value);
            cloneValue = Named.MODEL.isAncestor(model) ? cloneNamedMap.get(value.id) : undefined; // We still want to copy over here
            if (!cloneValue) { // Still did not get what we wanted?
                cloneValue = this.mirror(value);
            }

            objectQueue.push(value);
            cloneQueue.push(cloneValue);

            if (model.referable) {
                referableMap.set(value, cloneValue);
            }

            cloneSet.add(cloneValue);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static cloneReferencedSet(valueSet: Set<any>, cloneSet: Set<any>,
        referableMap: Map<{}, {}>, sourceConceptNameds: Set<Named>): void {

        if (valueSet.size === 0 && cloneSet.size === 0) {
            return;
        }
        cloneSet.clear();

        let cloneValue: {} | undefined;
        // Walk through existing array entries
        for (const value of valueSet) {
            const model = Metamodel.modelOf(value);
            cloneValue = referableMap.get(value);

            if (!cloneValue && Owned.MODEL.isAncestor(model)) {
                const concept = (value as Owned).concept as Named;
                // If we are copying in the vicinity of the concept objects passed in
                // we copy else we reference what the source has
                cloneValue = (concept && !sourceConceptNameds.has(concept)) ? value : undefined;
            }

            if (cloneValue) {
                cloneSet.add(cloneValue);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static cloneContainedMap(valueMap: Map<any, any>, cloneMap: Map<any, any>,
        objectQueue: Array<{}>, cloneQueue: Array<{}>, referableMap: Map<{}, {}>): void {
        if (valueMap.size === 0 && cloneMap.size === 0) {
            return;
        }

        const cloneNamedMap = new Map<number, Named>();

        // Store old values to lookup
        for (const [key, value] of cloneMap) {
            if (Named.MODEL.isInstance(key)) {
                cloneNamedMap.set((key as Named).id, key);
            }
            if (value && Named.MODEL.isInstance(value)) {
                cloneNamedMap.set((value as Named).id, key);
            }
        }

        cloneMap.clear();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let cloneKey: any | undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let cloneValue: any;

        // Walk through existing array entries
        for (const [key, value] of valueMap) {
            if (!Metamodel.isModelOf(key)) {
                cloneKey = key;
            } else {
                const model = Metamodel.modelOf(key);
                cloneKey = Named.MODEL.isAncestor(model) ? cloneNamedMap.get(key.id) : undefined; // We still want to copy over here
                if (!cloneKey) { // Still did not get what we wanted?
                    cloneKey = this.mirror(key);
                }

                objectQueue.push(key);
                cloneQueue.push(cloneKey);

                if (model.referable) {
                    referableMap.set(key, cloneKey);
                }
            }

            if (value === undefined) {
                cloneMap.set(cloneKey, undefined);
                continue;
            }

            if (!Metamodel.isModelOf(value)) {
                cloneValue = value;
            } else {
                const model = Metamodel.modelOf(value);
                cloneValue = Named.MODEL.isAncestor(model) ? cloneNamedMap.get(value.id) : undefined; // We still want to copy over here
                if (!cloneValue) { // Still did not get what we wanted?
                    cloneValue = this.mirror(value);
                }

                objectQueue.push(value);
                cloneQueue.push(cloneValue);

                if (model.referable) {
                    referableMap.set(value, cloneValue);
                }
            }

            cloneMap.set(cloneKey, cloneValue);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static cloneReferencedMap(valueMap: Map<any, any>, cloneMap: Map<any, any>,
        referableMap: Map<{}, {}>, sourceConceptNameds: Set<Named>): void {

        if (valueMap.size === 0 && cloneMap.size === 0) {
            return;
        }
        cloneMap.clear();

        let cloneKey: {} | undefined;
        let cloneValue: {} | undefined;
        let model: IModel;
        // Walk through existing array entries
        for (const [key, value] of valueMap) {
            model = Metamodel.modelOf(key);
            cloneKey = referableMap.get(key);

            if (!cloneKey && Owned.MODEL.isAncestor(model)) {
                const concept = (key as Owned).concept as Named;
                // If we are copying in the vicinity of the concept objects passed in
                // we copy else we reference what the source has
                cloneKey = (concept && !sourceConceptNameds.has(concept)) ? key : undefined;
            }

            model = Metamodel.modelOf(value);
            cloneValue = referableMap.get(value);

            if (!cloneValue && Owned.MODEL.isAncestor(model)) {
                const concept = (value as Owned).concept as Named;
                // If we are copying in the vicinity of the concept objects passed in
                // we copy else we reference what the source has
                cloneValue = (concept && !sourceConceptNameds.has(concept)) ? value : undefined;
            }

            if (cloneKey !== undefined) {
                cloneMap.set(cloneKey, cloneValue);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static mapContainedArray(valueArray: any[], cloneArray: any[],
        objectQueue: Array<{}>, cloneQueue: Array<{}>): void {
        if (valueArray.length === 0 && cloneArray.length === 0) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let value: any;

        // Derived array has other attributes
        for (let index = 0; index < valueArray.length; index++) {
            value = valueArray[index];
            if (!Metamodel.isModelOf(value)) {
                continue;
            }
            objectQueue.push(value);
            cloneQueue.push(cloneArray[index]);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static mapContainedSet(valueSet: Set<any>, cloneSet: Set<any>,
        objectQueue: Array<{}>, cloneQueue: Array<{}>, referableMap: Map<{}, {}>): void {
        if (valueSet.size === 0 && cloneSet.size === 0) {
            return;
        }

        const cloneNamedMap = new Map<number, Named>();
        for (const cloneValue of cloneSet) {
            if (!Named.MODEL.isInstance(cloneValue)) {
                continue;
            }
            cloneNamedMap.set((cloneValue as Named).id, cloneValue);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let valueClone: any;
        for (const value of valueSet) {
            valueClone = (Named.MODEL.isInstance(value)) ?
                cloneNamedMap.get((value as Named).id) :
                referableMap.get(value);

            if (valueClone !== undefined) {
                objectQueue.push(value);
                cloneQueue.push(valueClone);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static mapContainedMap(valueMap: Map<any, any>, cloneMap: Map<any, any>,
        objectQueue: Array<{}>, cloneQueue: Array<{}>, referableMap: Map<{}, {}>): void {
        if (valueMap.size === 0 && cloneMap.size === 0) {
            return;
        }
        const cloneNamedMap = new Map<number, Named>();
        for (const [cloneKey, cloneValue] of cloneMap) {
            if (Named.MODEL.isInstance(cloneKey)) {
                cloneNamedMap.set((cloneKey as Named).id, cloneKey);
            }
            if (Named.MODEL.isInstance(cloneValue)) {
                cloneNamedMap.set((cloneValue as Named).id, cloneValue);
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let valueClone: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let keyClone: any;
        for (const [key, value] of valueMap) {
            keyClone = (Named.MODEL.isInstance(key)) ?
                cloneNamedMap.get((value as Named).id) :
                referableMap.get(value);

            valueClone = (Named.MODEL.isInstance(value)) ?
                cloneNamedMap.get((value as Named).id) :
                referableMap.get(value);

            if (keyClone !== undefined) {
                objectQueue.push(key);
                cloneQueue.push(keyClone);
            }

            if (valueClone !== undefined) {
                objectQueue.push(value);
                cloneQueue.push(valueClone);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static isVoid(value: any): boolean {
        return value === undefined;
    }

    /**
     * Deep copy the state from sourceNamed and paste it to targetNamed
     */
    private static copyState(sourceNamed: Named, targetNamed: Named): void {
        const modelState = _.cloneDeep(sourceNamed.extension._state$);
        if (modelState && targetNamed.extension) {
            targetNamed.extension._state$ = modelState;
        }
    }
}
